/**
 * Rows for the results table, read straight off the request-line ledger.
 *
 * The ledger (ADR 0007) is the backend's account of what the customer asked
 * for: one line per request, each with a stable id, an ordinal that never
 * moves, a label frozen when the line was created, and an outcome. Every card
 * names the line it answers. So a table can be built by reading, not by
 * guessing which result belongs to which request — which is what the older
 * name-matching path did, and why a re-worded part could silently drop a row
 * that had already rendered correctly.
 *
 * Everything here is pure: same message in, same rows out, for ever.
 */
import { PartResult, RequestLine } from '../components/features/chat/types';
import type { SummaryItem } from '../components/features/chat/PartsTrackerPanel';

/** The tracker-row fields a line can fill in. Type-only dependency. */
export type TrackerItem = Pick<SummaryItem, 'name' | 'displayLabel' | 'originalQuery' | 'status' | 'n'>;

/** Lines that produced at least one catalog card. */
export const isFound = (line: RequestLine): boolean => line.status === 'found';

/** A line the customer skipped the question for ("Skip for now"): open,
 *  nothing guessed, answered later from the result. */
export const isSkipped = (line: Pick<RequestLine, 'status' | 'reason'> | null | undefined): boolean =>
  !!line && line.status === 'needs_answer' && line.reason === 'SKIPPED';

/** Lines the customer withdrew are not part of the order any more. */
export const isLive = (line: RequestLine): boolean => line.status !== 'removed';

/** A found line and the cards answering it, in the shape the table renders. */
export interface LineGroup {
  key: string;
  /** The resolved part name — the muted half of the Requested cell. */
  label: string;
  /** Indices into this message's `parts_results`. More than one = "N options". */
  indices: number[];
  num: number;
  /** Position among the groups, for stable React keys and anchors. */
  gi: number;
  line: RequestLine;
}

export type LedgerRow =
  | { kind: 'found'; num: number; ord: number; group: LineGroup; line: RequestLine }
  | { kind: 'not_found'; num: number; ord: number; term: string; line: RequestLine }
  | {
      kind: 'history'; num: number; ord: number; line: RequestLine;
      /** Hero card from an earlier message; absent when this session predates
       *  card→line stamping, in which case the row shows status only. */
      part?: PartResult; parts: PartResult[]; term: string;
    };

export interface LedgerRows {
  /** This turn's rows, in the customer's request order. */
  rows: LedgerRow[];
  /** The rest of the order, for the collapsed "full order" view. */
  historyRows: LedgerRow[];
  /** Found and total for THIS search. */
  turnFound: number;
  turnTotal: number;
  /** Found and total for the whole order. */
  orderFound: number;
  orderTotal: number;
}

/** Map every card to the line it answers. Cards with no line are left out — a
 *  card that cannot name its line has nothing to bind to. */
export function cardsByLine(cards: readonly PartResult[] | null | undefined): Map<string, number[]> {
  const byLine = new Map<string, number[]>();
  (cards ?? []).forEach((card, index) => {
    const id = (card?.line_id || '').trim();
    if (!id) return;
    const at = byLine.get(id);
    if (at) at.push(index);
    else byLine.set(id, [index]);
  });
  return byLine;
}

/**
 * Which of this message's cards answer each line.
 *
 * A line names the cards that answer it, so that is read first: two requested
 * lines can share one card when the search deduped them into a single lookup,
 * and a card can only claim one line itself. The card's own `line_id` is the
 * fallback for anything the line did not name.
 */
export function bindCards(
  lines: readonly RequestLine[],
  cards: readonly PartResult[] | null | undefined,
): Map<string, number[]> {
  const byUuid = new Map<string, number>();
  (cards ?? []).forEach((card, index) => {
    const uuid = (card?.session_part_uuid || '').trim();
    if (uuid && !byUuid.has(uuid)) byUuid.set(uuid, index);
  });
  const byLineId = cardsByLine(cards);
  const bindings = new Map<string, number[]>();
  for (const line of lines) {
    const named = (line.parts ?? [])
      .map(uuid => byUuid.get(String(uuid).trim()))
      .filter((i): i is number => i != null);
    const indices = named.length ? named : (byLineId.get(line.line_id) ?? []);
    if (indices.length) bindings.set(line.line_id, indices);
  }
  return bindings;
}

/** True when this message carries a ledger and can render from it. */
export function hasLedger(lines?: RequestLine[] | null): boolean {
  return Array.isArray(lines) && lines.length > 0;
}

/**
 * Split the ledger into this turn's rows and the rest of the order.
 *
 * `turnLines` decides which lines this message is ABOUT, and a message that
 * does not say is about none of them. Treating an unscoped ledger as the whole
 * order is how a VIN bubble ended up drawing the search's results table.
 */
export function buildLedgerRows(args: {
  lines: readonly RequestLine[];
  turnLines?: readonly string[] | null;
  /** This message's own cards. */
  cards: readonly PartResult[] | null | undefined;
  /** Cards from earlier messages, for the rest of the order. */
  priorCards?: readonly PartResult[] | null;
  /** Which cards answer which line, when the cards cannot say so themselves —
   *  a derived ledger knows the binding because it built the lines FROM the
   *  cards. Defaults to reading `line_id` off each card. */
  bindings?: Map<string, number[]> | null;
}): LedgerRows {
  const { lines, turnLines, cards, priorCards, bindings } = args;
  const live = lines.filter(isLive).slice().sort((a, b) => (a.n || 0) - (b.n || 0));
  const inTurn = new Set(turnLines ?? []);

  const here = bindings ?? cardsByLine(cards);
  // The rest of the order binds the same way this turn does: the line's own
  // card uuids first, the card's `line_id` stamp second. Stamp-only binding
  // missed every line re-identified after its card was loaded (a clarification
  // pick that split or renamed it), and the row fell back to a bare
  // "Found earlier" with no part.
  const earlier = bindCards(live, priorCards);
  const thisTurn = bindCards(live, cards);

  const rows: LedgerRow[] = [];
  const historyRows: LedgerRow[] = [];
  let ord = 0;
  let gi = 0;

  for (const line of live) {
    const num = line.n || 0;
    if (inTurn.has(line.line_id)) {
      const indices = here.get(line.line_id) ?? [];
      if (isFound(line) && indices.length > 0) {
        rows.push({
          kind: 'found', num, ord: ord++, line,
          group: { key: line.line_id, label: line.label || line.canonical || line.original, indices, num, gi: gi++, line },
        });
      } else {
        // A line the search did not answer with a card in this message. That
        // includes a found line whose cards were loaded earlier: it still has
        // its own status, and the row says so rather than claiming a miss.
        rows.push({ kind: 'not_found', num, ord: ord++, term: line.original, line });
      }
      continue;
    }
    // The rest of the order, as of this message.
    // Earlier messages first; a line deduped into one of THIS message's cards
    // still has that card to show.
    const priorIdx = earlier.get(line.line_id) ?? [];
    const parts = (priorIdx.length
      ? priorIdx.map(i => (priorCards ?? [])[i])
      : (thisTurn.get(line.line_id) ?? []).map(i => (cards ?? [])[i])
    ).filter(Boolean) as PartResult[];
    historyRows.push({
      kind: 'history', num, ord: ord++, line, term: line.original,
      part: parts[0], parts,
    });
  }

  const turnLive = live.filter(l => inTurn.has(l.line_id));
  return {
    rows,
    historyRows,
    turnFound: turnLive.filter(isFound).length,
    turnTotal: turnLive.length,
    orderFound: live.filter(isFound).length,
    orderTotal: live.length,
  };
}

/** How a line's outcome reads in the table. `tone` picks the icon and colour;
 *  `key`/`fallback` are the i18n pair for the label, `hint` for the tooltip. */
export interface LineChip {
  tone: 'ok' | 'waiting' | 'unconfirmed' | 'missing' | 'fault' | 'searching';
  key: string;
  fallback: string;
  hintKey: string;
  hintFallback: string;
}

/**
 * The chip for a line, from its own status and reason.
 *
 * Never a bare "not found": a search that did not confirm a part has not proved
 * the catalog lacks it, and a catalog that never answered has proved nothing at
 * all. Each state says what happened and implies what to do next.
 */
export function lineChip(line: Pick<RequestLine, 'status' | 'reason'>): LineChip {
  switch (line.status) {
    case 'found':
      return { tone: 'ok', key: 'chat.multiPart.lineFoundEarlier', fallback: 'Found earlier',
        hintKey: 'chat.multiPart.lineFoundEarlierHint', hintFallback: 'Found in an earlier search in this order.' };
    case 'searching':
      return { tone: 'searching', key: 'chat.multiPart.lineSearching', fallback: 'Searching…',
        hintKey: 'chat.multiPart.lineSearchingHint', hintFallback: 'Looking this part up in the catalog.' };
    case 'needs_answer':
      if (line.reason === 'SKIPPED') {
        return { tone: 'unconfirmed', key: 'chat.multiPart.lineSkipped', fallback: 'Needs your input',
          hintKey: 'chat.multiPart.lineSkippedHint',
          hintFallback: "Clarification skipped — CATA didn't guess. Pick the part from the diagram or give the AI chat more context to fill this row." };
      }
      return { tone: 'waiting', key: 'chat.multiPart.lineNeedsAnswer', fallback: 'Waiting for your answer',
        hintKey: 'chat.multiPart.lineNeedsAnswerHint', hintFallback: 'Answer the question above and this line continues.' };
    case 'not_in_catalog':
      return { tone: 'missing', key: 'chat.multiPart.lineNotInCatalog', fallback: 'Not in the OE catalog',
        hintKey: 'chat.multiPart.lineNotInCatalogHint', hintFallback: 'The catalog for this vehicle carries no such part.' };
    case 'catalog_error':
      return line.reason === 'SEARCH_INCOMPLETE'
        ? { tone: 'fault', key: 'chat.multiPart.reasonIncomplete', fallback: 'Search incomplete',
            hintKey: 'chat.multiPart.reasonIncompleteHint', hintFallback: 'The search did not complete for this part — retry it.' }
        : { tone: 'fault', key: 'chat.multiPart.reasonCatalogUnavailable', fallback: 'Catalog unavailable',
            hintKey: 'chat.multiPart.reasonCatalogUnavailableHint', hintFallback: "The parts catalog didn't respond for this part — retry it." };
    case 'no_match':
    default:
      return line.reason === 'NO_MATCHING_SECTION'
        ? { tone: 'unconfirmed', key: 'chat.multiPart.reasonNeedsDetail', fallback: 'Needs detail',
            hintKey: 'chat.multiPart.reasonNeedsDetailHint', hintFallback: "Couldn't map this to a catalog section — describe where it sits or rephrase." }
        : { tone: 'unconfirmed', key: 'chat.multiPart.noConfirmedMatch', fallback: 'No confirmed match',
            hintKey: 'chat.multiPart.noConfirmedMatchHint', hintFallback: 'This request remains unresolved. A search miss does not prove the part is absent from the catalog.' };
  }
}

// ── Deriving a ledger for messages that predate it ──────────────────────────
// A message from before the ledger still says everything a row needs, on the
// cards themselves: which request each card was loaded for, and which requests
// came back empty. Deriving a ledger from that gives the whole app ONE way to
// render a result, instead of a second pairing path kept alive for old
// messages. What it cannot recover is the order's shape beyond this message —
// that is what `ledgerFromConversation` walks the messages for.

/** What a card says about the request it answers. */
const cardRequest = (card: PartResult): string =>
  String(card.related_user_query || (card as any).original_name || card.related_corrected_name || '').trim();

/** A card's own identity: its resolved name plus the qualifiers the customer
 *  stated. Two cards that agree on both are options for one request. This is
 *  card-to-card, never card-to-request — nothing is being paired here. */
const cardIdentity = (card: PartResult, positions: (c: PartResult) => string[]): string => {
  const quals = [
    ...(card.part_parameters || []).map(p => String(p).trim().toLowerCase()),
    ...positions(card).map(p => p.toLowerCase()),
  ].sort().join('|');
  const name = String(card.related_corrected_name || card.part_name || cardRequest(card) || '').trim().toLowerCase();
  return `${name}\u001f${quals}`;
};

/** Reason code → the status it implies, for a message that has reasons but no
 *  lines. Mirrors the backend's own mapping. */
function statusForReason(reason?: string | null): RequestLine['status'] {
  if (reason === 'NOT_IN_CATALOG') return 'not_in_catalog';
  if (reason === 'CATALOG_UNAVAILABLE' || reason === 'PART_LOAD_FAILED' || reason === 'SEARCH_INCOMPLETE') {
    return 'catalog_error';
  }
  return 'no_match';
}

export interface MessageLedgerInput {
  lines?: RequestLine[] | null;
  turn_lines?: string[] | null;
  parts_results?: PartResult[] | null;
  not_found_parts?: string[] | null;
  not_found_reasons?: Record<string, string> | null;
  /** The turn's request list, used only to put derived lines back into the
   *  order the customer asked in. Matched on the exact term, never fuzzily. */
  requested_parts?: string[] | null;
}

/**
 * The ledger for one message: the backend's when it sent one, otherwise one
 * derived from the message's own cards and misses.
 *
 * A derived ledger describes only what this message is about, which is exactly
 * what its table should show. Line ids are synthetic and local to the message;
 * they exist so cards can bind to rows, not to be compared across messages.
 */
export function ledgerFromMessage(
  msg: MessageLedgerInput,
  positions: (card: PartResult) => string[] = () => [],
  /** False when the conversation already has a real ledger: a message with no
   *  ledger of its own and no cards is narration (the summary bubble after a
   *  search), not a second copy of the results. Cards are always rendered. */
  derive = true,
): { lines: RequestLine[]; turnLines: string[]; bindings: Map<string, number[]> } {
  if (hasLedger(msg.lines)) {
    return {
      lines: msg.lines as RequestLine[],
      turnLines: (msg.turn_lines ?? []) as string[],
      bindings: bindCards(msg.lines as RequestLine[], msg.parts_results),
    };
  }
  if (!derive && !(msg.parts_results?.length)) {
    return { lines: [], turnLines: [], bindings: new Map() };
  }
  const lines: RequestLine[] = [];
  const byKey = new Map<string, RequestLine>();
  const bindings = new Map<string, number[]>();
  (msg.parts_results ?? []).forEach((card, index) => {
    const key = String(card.line_id || '').trim() || cardIdentity(card, positions);
    const seen = byKey.get(key);
    if (seen) {
      seen.parts = [...(seen.parts ?? []), String(index)];
      bindings.set(seen.line_id, [...(bindings.get(seen.line_id) ?? []), index]);
      return;
    }
    const request = cardRequest(card);
    const label = String(card.related_corrected_name || card.part_name || request || '').trim();
    const line: RequestLine = {
      line_id: String(card.line_id || '').trim() || `derived-${index}`,
      n: lines.length + 1,
      original: request || label,
      label: label || request,
      canonical: String(card.related_corrected_name || '').trim() || label,
      params: (card.part_parameters ?? []).map(String),
      status: 'found',
      reason: null,
      parts: [String(index)],
      picked_by_user: Boolean((card as any).user_selected),
      question_id: null,
    };
    byKey.set(key, line);
    bindings.set(line.line_id, [index]);
    lines.push(line);
  });
  (msg.not_found_parts ?? []).forEach((term, i) => {
    const name = String(term || '').trim();
    if (!name) return;
    const reason = msg.not_found_reasons?.[name] ?? null;
    lines.push({
      line_id: `derived-miss-${i}`,
      n: lines.length + 1,
      original: name, label: name, canonical: name, params: [],
      status: statusForReason(reason), reason,
      parts: [], picked_by_user: false, question_id: null,
    });
  });
  // Put the lines back into the order the customer asked in, where the request
  // list names them exactly. Cards come back before misses otherwise, which is
  // the order they were built in, not the order they were asked in.
  const asked = (msg.requested_parts ?? []).map(t => String(t || '').trim().toLowerCase());
  if (asked.length) {
    const rank = (line: RequestLine) => {
      const at = asked.indexOf(line.original.trim().toLowerCase());
      return at < 0 ? asked.length + lines.indexOf(line) : at;
    };
    lines.sort((a, b) => rank(a) - rank(b));
    lines.forEach((line, i) => { line.n = i + 1; });
  }
  return { lines, turnLines: lines.map(l => l.line_id), bindings };
}

/**
 * The order as the whole conversation knows it: every line any message
 * accounted for, in the order they first appeared, each carrying its most
 * recent outcome.
 *
 * The tracker reads this. A message-level ledger is not enough for it — the
 * tracker names the whole order, not the last search.
 */
export function ledgerFromConversation(
  messages: readonly MessageLedgerInput[],
  positions: (card: PartResult) => string[] = () => [],
): RequestLine[] {
  // A real ledger already describes the whole order, with its own ordinals and
  // an outcome per line. The newest one wins outright: merging the guesses that
  // earlier messages made about the same parts would double them.
  for (let i = messages.length - 1; i >= 0; i--) {
    if (hasLedger(messages[i].lines)) return orderedLines(messages[i].lines as RequestLine[]);
  }
  const merged = new Map<string, RequestLine>();
  for (const msg of messages) {
    for (const line of ledgerFromMessage(msg, positions).lines) {
      const key = `${line.original.toLowerCase()}\u001f${(line.params ?? []).join('|')}`;
      const seen = merged.get(key);
      merged.set(key, seen ? { ...line, n: seen.n } : { ...line, n: merged.size + 1 });
    }
  }
  return Array.from(merged.values()).sort((a, b) => (a.n || 0) - (b.n || 0));
}

/** Every line of the order, in the customer's request order. Withdrawn lines
 *  stay in, struck through — the tracker shows what was asked, including what
 *  was taken back. */
export function orderedLines(lines: readonly RequestLine[]): RequestLine[] {
  return lines.slice().sort((a, b) => (a.n || 0) - (b.n || 0));
}

/** One tracker row for one line.
 *
 * `name` stays the search-language name because cart membership and edit flows
 * match on it; `displayLabel` keeps the established "Resolved Name (original)"
 * shape that every render point already knows how to flip.
 */
export function trackerItem(line: RequestLine): TrackerItem {
  const name = line.canonical || line.label || line.original;
  const shown = line.label || name;
  return {
    name,
    // The line's own number. Nothing recomputes it, so a row cannot be numbered
    // differently in the tracker and in the results table.
    n: line.n,
    displayLabel: line.original && line.original.toLowerCase() !== shown.toLowerCase()
      ? `${shown} (${line.original})`
      : shown,
    originalQuery: line.original,
    status: line.status === 'removed'
      ? 'removed'
      : line.status === 'not_in_catalog'
        ? 'not_in_oe'
        : line.status === 'needs_answer' ? 'warning' : 'normal',
  };
}

/** The lines a search left unsettled, named as the tracker names them, so the
 *  panel can mark those rows without matching text. */
export function unsettledNames(lines: readonly RequestLine[]): string[] {
  return lines
    .filter(l => l.status === 'no_match' || l.status === 'catalog_error')
    .map(l => l.canonical || l.label || l.original)
    .filter(Boolean);
}

/** True when a message has an outcome worth a table: a card, or a line the
 *  search actually settled. A clarification pause has neither — every line is
 *  waiting on the customer — and its question form is the whole UI for that
 *  state; a table of "waiting" rows under it only pushes the question away. */
export function hasSettledOutcome(rows: readonly LedgerRow[]): boolean {
  return rows.some(r =>
    r.kind === 'found'
    // A skipped line IS an outcome: the result says it stays open.
    || (r.kind === 'not_found' && (isSkipped(r.line)
      || (r.line.status !== 'needs_answer' && r.line.status !== 'searching'))));
}

/** Lines worth offering a re-search on — the ones a search failed to settle. */
export function researchableTerms(rows: readonly LedgerRow[]): string[] {
  return rows
    .filter(r => r.kind === 'not_found'
      && r.line.status !== 'found'
      && r.line.status !== 'searching'
      && r.line.status !== 'needs_answer')
    .map(r => (r as { term: string }).term)
    .filter(Boolean);
}
